/**
 * @file A label and a plain definition for every analytics metric, keyed by
 * `AnalyticsMetricId`. Consumers: the Overview tile tooltips, the Pipeline by
 * stage card, the Projects, Orders and Invoicing headings, and the Metrics
 * tab's selected-metric tooltip, which reads the metric behind its series
 * query, so `salesClosed` defines booked, `salesCredits` orders written and
 * quotes issued. The `Record` type and a
 * test both fail when a metric id has no description, so a metric never
 * reaches the screen unexplained.
 */
import type { AnalyticsMetricId } from '#types';

import type { AnalyticsMetricDescription } from './index.types';

export const ANALYTICS_METRIC_DESCRIPTIONS: Record<
  AnalyticsMetricId,
  AnalyticsMetricDescription
> = {
  salesClosed: {
    label: 'Booked sales',
    definition:
      'Order lines with a released status, counted on their release date. Booked is each line\'s whole amount, counted once. Sales credit is each salesperson\'s share of those lines, and a line credited to nobody adds no credit. At My sales, booked and the goal show your sales credit.',
  },
  salesCredits: {
    label: 'Sales credit rows',
    definition:
      'Orders written counts the order forms with a credit row, and Quotes issued the forms in a quote stage with a credit row, each on its sales credit date.',
  },
  quoteLines: {
    label: 'Quoted value',
    definition:
      'The line value of quotes and buys in the period, with Quote and Buy kept apart.',
  },
  releaseLines: {
    label: 'Released value',
    definition:
      'The line value of orders in the period, split into held and released lines.',
  },
  openForms: {
    label: 'Open quotes and buys',
    definition:
      'Quotes and buys not in a closed state that no order descends from, with their line value.',
  },
  conversion: {
    label: 'Conversion',
    definition:
      'Quotes in the period by kind, and how many of them an order descends from.',
  },
  projects: {
    label: 'Projects',
    definition:
      "Each project's furthest open stage among the forms you can see, valued by its open forms at that stage only. A project is Complete once a reconciliation is closed, and Lost when every form is closed.",
  },
  pipeline: {
    label: 'Pipeline',
    definition:
      "Every form type with lines, valued by the whole amount of its forms' lines (quoted for quotes and buys, ordered otherwise). Order types split into released and not released lines. A project with forms of several types counts under each, so rows never add up.",
  },
  orderLines: {
    label: 'Order lines',
    definition:
      'The lines of Order forms: open lines, the held, released and shipped lines with their values, and how many are past their promised date without a fulfilled status. Values are your credited share at My sales and My team and the whole line value at Org.',
  },
  invoiceLines: {
    label: 'Invoices',
    definition:
      'Invoices on order lines: invoiced in the period, outstanding and how far past due, with your credited share and the whole amount.',
  },
};
